import { Link, NavLink, useNavigate } from 'react-router-dom';
import { MdShoppingCart, MdViewList, MdTaskAlt, MdImportExport, MdLogout, MdBarChart, MdCalculate, MdSettings, MdLocalShipping, MdFactCheck, MdBuild, MdStore } from 'react-icons/md';
import { toast } from 'react-toastify';
import { clearAuthData } from '../../utils/auth';
import { api } from '../../utils/api';
import Button from './Button';
import { postMessageToLeboncoinExt } from '../../lib/leboncoin/LeboncoinExtApi';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback } from 'react';
import { useRequestManager } from './RequestManager';

const GET_EBAY_SHOPS_QUERY = gql`
  query MainNav_GetEbayShops {
    shops {
      id
      shopType
      shopKey
      credentials
    }
  }
`;

const UPDATE_SHOP_MUTATION = gql`
  mutation MainNav_UpdateShop($id: ID!, $input: UpdateShopInput!) {
    updateShop(id: $id, input: $input) {
      id
    }
  }
`;

type NavSubItem = { label: string; url: string; icon?: React.ReactNode };
type NavItem = {
  label: string;
  url: string | null;
  icon: React.ReactNode;
  children?: NavSubItem[];
};

const navItems: NavItem[] = [
  { label: 'Ads', url: '/ads', icon: <MdViewList /> },
  {
    label: 'Orders',
    url: '/orders',
    icon: <MdShoppingCart />,
  },
  { label: 'Import Orders', url: '/import-orders', icon: <MdImportExport /> },
  { label: 'Restock Shipments', url: '/restock-shipments', icon: <MdShoppingCart /> },
  { label: 'Tasks', url: '/tasks', icon: <MdTaskAlt /> },
  { label: 'Shops', url: '/shops', icon: <MdStore /> },
  {
    label: 'Tools',
    url: null,
    icon: <MdBuild />,
    children: [
      { label: 'Calculator', url: '/calculator', icon: <MdCalculate /> },
      { label: 'Shipping Rates', url: '/shipping-rates', icon: <MdLocalShipping /> },
      { label: 'Processes', url: '/processes', icon: <MdSettings /> },
      { label: 'Stats', url: '/stats', icon: <MdBarChart /> },
      { label: 'Orders Stats', url: '/orders-stats', icon: <MdBarChart /> },
      { label: 'Import Tx', url: '/import-tx', icon: <MdImportExport /> },
      { label: 'Verify Data Integrity', url: '/verify-data-integrity', icon: <MdFactCheck /> },
    ]
  },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  isActive
    ? 'h-16 border-yellow-500 text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'
    : 'h-16 border-transparent text-gray-900 hover:border-yellow-500 hover:text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium';

export function Navbar() {
  const navigate = useNavigate();
  const { trackRequest } = useRequestManager();

  const { data: shopsData } = useQuery(GET_EBAY_SHOPS_QUERY);
  const [updateShopMutation] = useMutation(UPDATE_SHOP_MUTATION);

  const handleLogout = useCallback(async () => {
    try {
      await api.post('/logout');
      
      clearAuthData();
      
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearAuthData();
      toast.success('Logged out successfully');
      navigate('/login');
    }
  }, [navigate]);


  const handleRefreshEbayPacklinkToken = useCallback(async () => {
    const ebayShop = shopsData?.shops?.find((shop: { shopType: string }) => shop.shopType === 'ebay');
    if (!ebayShop) {
      toast.error('No eBay shop found');
      return;
    }

    trackRequest(
      (async () => {
        const token = await postMessageToLeboncoinExt(window, {
          action: 'get-ebay-packlink-token',
        });
        await updateShopMutation({
          variables: {
            id: ebayShop.id,
            input: {
              credentials: {
                ...(ebayShop.credentials || {}),
                packlinkToken: token,
              },
            },
          },
        });
      })(),
      {
        title: 'Refreshing Ebay Packlink Token',
        isBlocking: true,
      }
    );
  }, [shopsData, updateShopMutation, trackRequest]);

  return (
    <nav className="px-4 bg-white shadow-xs flex justify-between">
      <div className="shrink-0 flex items-center">
        <Link to="/" className="text-xl font-bold text-gray-900">
          Wow Magasin
        </Link>
      </div>
      <div className="hidden sm:ml-6 sm:flex flex-1 sm:space-x-8 flex-wrap items-stretch">
        {navItems.map((item) =>
          item.children?.length ? (
            <div key={item.url} className="relative group">
              <NavLink
                to={item.url || ''}
                className={navLinkClass}
                end={item.url === '/'}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </NavLink>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-[visibility,opacity] duration-150 z-50">
                <div className="bg-white shadow-lg border border-gray-200 rounded-md py-1 min-w-[180px] mt-0">
                  {item.children.map((sub) => (
                    <Link
                      key={sub.url}
                      to={sub.url}
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      {sub.icon && <span className="text-gray-500">{sub.icon}</span>}
                      {sub.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <NavLink
              key={item.url}
              to={item.url}
              className={navLinkClass}
              end={item.url === '/'}
            >
              <span className="mr-2">{item.icon}</span>
              {item.label}
            </NavLink>
          )
        )}
      </div>
      <div className="flex items-center ml-6">
        {/* <Button preset="white" onClick={handleRefreshEbayPacklinkToken}>
          Refresh Ebay Packlink Token
        </Button> */}
        <Button
          preset='white'
          onClick={handleLogout}
        >
          <MdLogout className="mr-2" />
          Logout
        </Button>
      </div>
    </nav>
  );
}
